/**
 * Reusable wizard step indicator (P2-7, UX-REVIEW §4 item 7).
 *
 * Extracted from the inline stepper in CreateDeliverablePage so every multi-step flow
 * shows the same accessible progress affordance. Renders a `role="list"` of steps; the
 * active step carries `aria-current="step"` and each step exposes its position + state
 * to assistive tech ("Step 2 of 4, current"). Completed steps show a check; upcoming
 * steps are dimmed.
 */
import { useTranslation } from "react-i18next";

export interface StepperProps {
  /** Plain-language label per step, in order. Length defines the total. */
  steps: string[];
  /** 1-based index of the active step. */
  current: number;
}

export function Stepper({ steps, current }: StepperProps) {
  const { t } = useTranslation();
  const total = steps.length;

  return (
    <ol
      className="wizard-steps"
      role="list"
      aria-label={t("stepper.label", { current, total })}
    >
      {steps.map((label, i) => {
        const n = i + 1;
        const state = n < current ? "done" : n === current ? "active" : "todo";
        const stateText =
          state === "done"
            ? t("stepper.completed")
            : state === "active"
              ? t("stepper.current")
              : t("stepper.upcoming");
        return (
          <li
            key={label}
            className={`wizard-step wizard-step--${state}`}
            aria-current={n === current ? "step" : undefined}
          >
            <span className="wizard-step__num" aria-hidden="true">
              {state === "done" ? "✓" : n}
            </span>
            <span className="wizard-step__label">{label}</span>
            {/* Position + state for screen readers, visually hidden. */}
            <span className="sr-only">
              {t("stepper.stepState", { current: n, total, state: stateText })}
            </span>
          </li>
        );
      })}
    </ol>
  );
}
